import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import AuthCard from "@/components/jea/AuthCard";
import JeaButton from "@/components/jea/JeaButton";
import { Input } from "@/components/ui/input";
import { magicLinkApi, formatApiError, writeAuth } from "@/lib/apiClient";

const useQueryToken = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  return params.get("token") || "";
};

const AccountRecoverPage = () => {
  const token = useQueryToken();
  const nav = useNavigate();

  const [stage, setStage] = useState("validating"); // validating | ready | expired | saving | done
  const [email, setEmail] = useState("");
  const [requires2FA, setRequires2FA] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setStage("expired");
      setError("No token provided. Request a new recovery link from your administrator.");
      return;
    }
    (async () => {
      try {
        const res = await magicLinkApi.validate(token);
        setEmail(res.email || "");
        setRequires2FA(!!res.two_factor_required_on_next_login);
        setStage("ready");
      } catch (err) {
        setError(formatApiError(err, "This recovery link is invalid or has expired."));
        setStage("expired");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setError("");
    setStage("saving");
    try {
      const res = await magicLinkApi.consume(token, password);
      // Persist the issued session.
      writeAuth({
        access_token: res.access_token,
        refresh_token: res.refresh_token,
        user: res.user,
      });
      toast.success("Password updated. Welcome back.");
      setStage("done");
      // Route decision: if 2FA re-enrollment required → settings/2fa, else dashboard.
      setTimeout(() => {
        if (res.two_factor_required_on_next_login) nav("/account/2fa", { replace: true });
        else nav("/dashboard", { replace: true });
      }, 1200);
    } catch (err) {
      setError(formatApiError(err));
      setStage("ready");
    }
  };

  if (stage === "validating") {
    return (
      <AuthCard>
        <div className="flex flex-col items-center gap-3 py-6" data-testid="recover-validating">
          <Loader2 className="w-6 h-6 text-success animate-spin" />
          <p className="text-[14px] text-text-secondary">Validating your recovery link…</p>
        </div>
      </AuthCard>
    );
  }

  if (stage === "expired") {
    return (
      <AuthCard>
        <div className="space-y-4 text-center" data-testid="recover-expired">
          <ShieldAlert className="w-10 h-10 text-danger mx-auto" />
          <h2 className="text-[20px] font-bold text-white">Link invalid or expired</h2>
          <p className="text-[14px] text-text-secondary">
            {error || "This recovery link cannot be used. Ask your administrator to send a fresh one."}
          </p>
          <JeaButton size="md" fullWidth onClick={() => nav("/login")} data-testid="recover-expired-login">
            Back to login
          </JeaButton>
        </div>
      </AuthCard>
    );
  }

  if (stage === "done") {
    return (
      <AuthCard>
        <div className="space-y-3 text-center py-6" data-testid="recover-done">
          <CheckCircle2 className="w-10 h-10 text-success mx-auto" />
          <h2 className="text-[20px] font-bold text-white">Password updated</h2>
          <p className="text-[14px] text-text-secondary">
            {requires2FA ? "Redirecting you to 2FA setup…" : "Redirecting you to the dashboard…"}
          </p>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <form onSubmit={submit} className="space-y-5" data-testid="recover-form">
        <div className="space-y-2 text-center">
          <h2 className="text-[20px] font-bold text-white">Set a new password</h2>
          <p className="text-[13px] text-text-secondary">
            Recovery link for <span className="text-white font-medium">{email}</span>
          </p>
          {requires2FA && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wider uppercase text-warning bg-warning/10 border border-warning/30 rounded-md px-2.5 py-1">
              2FA re-enrollment required after this
            </div>
          )}
        </div>

        <div className="relative">
          <Input
            type={showPwd ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="New password (min 8 characters)"
            autoComplete="new-password"
            data-testid="recover-new-password"
          />
          <button
            type="button"
            onClick={() => setShowPwd((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-success"
            tabIndex={-1}
            data-testid="recover-toggle-password"
          >
            {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        <Input
          type={showPwd ? "text" : "password"}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Confirm new password"
          autoComplete="new-password"
          data-testid="recover-confirm-password"
        />

        {error && (
          <div className="text-[13px] text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2" data-testid="recover-error">
            {error}
          </div>
        )}

        <JeaButton type="submit" size="lg" fullWidth loading={stage === "saving"} data-testid="recover-submit">
          Set password & continue
        </JeaButton>
      </form>
    </AuthCard>
  );
};

export default AccountRecoverPage;
